/**
 * Squid Router Integration for BTC bridging
 * Handles cross-chain swaps from native BTC to wrapped BTC (cbBTC/WBTC) on Ethereum
 * 
 * FIXED: Two-step process for Bitcoin deposits:
 * 1. Call /route WITH fromAddress (user's wallet) to get route
 * 2. Call /deposit-address with proper formatting to get Bitcoin deposit address
 * 
 * Based on working implementation from bcom-megapot
 * Integrator ID: bitcoin.com-9e4840c9-a3d5-4286-bb7b-a71baa2d7ea7
 */

import { Address } from '../types/contracts'

const SQUID_API_URL = (import.meta as any).env?.VITE_SQUID_ROUTER_API_URL || 'https://v2.api.squidrouter.com/v2'
const SQUID_INTEGRATOR_ID = (import.meta as any).env?.VITE_SQUID_INTEGRATOR_ID || 'bitcoin.com-9e4840c9-a3d5-4286-bb7b-a71baa2d7ea7'

export interface SquidRouteRequest {
  fromChain: string
  fromToken: string
  fromAmount: string
  toChain: string
  toToken: string
  toAddress: Address
  slippage?: number
  fromAddress?: Address | string
  enableBoost?: boolean
}

export interface SquidRoute {
  route: {
    estimate: {
      fromAmount: string
      toAmount: string
      toAmountMin: string
      exchangeRate: string
      aggregatePriceImpact: string
      estimatedRouteDuration: number
      gasCosts?: {
        type: string
        token: {
          chainId: string
          address: string
          name: string
          symbol: string
          decimals: number
          logoURI: string
        }
        amount: string
        amountUSD: string
      }[]
      feeCosts?: {
        name: string
        description: string
        percentage: string
        token: {
          chainId: string
          address: string
          name: string
          symbol: string
          decimals: number
          logoURI: string
        }
        amount: string
        amountUSD: string
      }[]
    }
    transactionRequest?: {
      routeType: string
      target: string
      data: string
      value: string
      gasLimit: string
      gasPrice: string
      maxFeePerGas?: string
      maxPriorityFeePerGas?: string
      request?: any // Chainflip nested request structure
    }
    params: {
      fromChain: string
      toChain: string
      fromToken: string
      toToken: string
      fromAmount: string
      toAmount: string
      fromAddress: string
      toAddress: string
      slippage: number
    }
  }
  requestId: string
}

export interface SquidDepositAddress {
  depositAddress: string
  amount: string
  chainflipStatusTrackingId: string
  estimatedRouteDuration: number
  exchangeRate: string
  toAmount: string
  toAmountMin: string
}

export interface SquidStatus {
  id: string
  status: 'needs_gas' | 'ongoing' | 'success' | 'partial_success' | 'not_found'
  fromChain?: {
    chainId: string
    transactionId: string
    blockNumber?: number
  }
  toChain?: {
    chainId: string
    transactionId?: string
    blockNumber?: number
  }
  axelarTransactionUrl?: string
  squidTransactionStatus: 'ongoing' | 'success' | 'failed'
  chainflipStatusTrackingId?: string
  error?: {
    message: string
  }
}

export interface BTCDepositHistory {
  id: string
  createdAt: number
  btcAmount: string
  depositAddress: string
  targetToken: 'cbBTC' | 'WBTC'
  estimatedOutput: string
  exchangeRate: string
  toAddress: Address
  chainflipStatusTrackingId: string
  status: 'pending' | 'deposited' | 'bridging' | 'completed' | 'failed'
  txHash?: string
  completedAt?: number
  errorMessage?: string
}

/**
 * Get a route from Squid Router (step 1)
 * For Bitcoin deposits, fromAddress MUST be included (user's wallet address)
 * The actual Bitcoin deposit address is generated separately via /deposit-address
 */
export async function getSquidRoute(
  request: SquidRouteRequest
): Promise<SquidRoute> {
  try {
    console.log('🔍 [Squid] getSquidRoute request:', {
      fromChain: request.fromChain,
      fromToken: request.fromToken,
      fromAmount: request.fromAmount,
      toChain: request.toChain,
      toToken: request.toToken,
      toAddress: request.toAddress,
      fromAddress: request.fromAddress,
    })
    
    // Build params - fromAddress MUST be included for Bitcoin
    const params: any = {
      fromChain: request.fromChain,
      fromToken: request.fromToken,
      fromAmount: request.fromAmount,
      toChain: request.toChain,
      toToken: request.toToken,
      toAddress: request.toAddress,
      slippage: request.slippage || 1,
      enableBoost: request.enableBoost !== false,
    }

    // Include fromAddress if provided (REQUIRED for Bitcoin)
    if (request.fromAddress) {
      params.fromAddress = request.fromAddress
    }

    console.log('📤 [Squid] Sending route request:', params)

    const response = await fetch(`${SQUID_API_URL}/route`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-integrator-id': SQUID_INTEGRATOR_ID,
      },
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('❌ [Squid] Route API error:', errorData)
      throw new Error(`Squid API error: ${JSON.stringify(errorData)}`)
    }

    const data = await response.json()
    console.log('✅ [Squid] Route received, requestId:', data.requestId)
    return data
  } catch (error) {
    console.error('Error fetching Squid route:', error)
    throw error
  }
}

/**
 * Get Bitcoin deposit address (step 2 - ONLY for Bitcoin deposits)
 * CRITICAL: Must wrap transactionRequest with proper Chainflip format
 */
export async function getSquidDepositAddress(
  route: any,
  requestId?: string
): Promise<SquidDepositAddress> {
  try {
    // Extract the transactionRequest from the route
    // It may be nested: route.request or just route
    const chainflipRequest = route.transactionRequest?.request || route.transactionRequest || route
    
    // CRITICAL: Wrap in Chainflip format as per working example
    const requestBody: any = {
      type: 'CHAINFLIP_DEPOSIT_ADDRESS',
      request: chainflipRequest,
    }
    
    // Add requestId if provided
    if (requestId) {
      requestBody.requestId = requestId
    }
    
    console.log('📤 [Squid] Requesting Chainflip deposit address:', {
      hasRequest: !!requestBody.request,
      hasRequestId: !!requestBody.requestId,
      requestType: requestBody.type,
    })

    const response = await fetch(`${SQUID_API_URL}/deposit-address`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-integrator-id': SQUID_INTEGRATOR_ID,
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('❌ [Squid] Deposit address error:', errorData)
      throw new Error(`Deposit address failed: ${JSON.stringify(errorData)}`)
    }

    const data = await response.json()
    console.log('✅ [Squid] Deposit address generated:', {
      depositAddress: data.depositAddress,
      amount: data.amount,
      chainflipStatusTrackingId: data.chainflipStatusTrackingId,
    })
    
    return data
  } catch (error) {
    console.error('Error getting Squid deposit address:', error)
    throw error
  }
}

/**
 * Check the status of a Squid transaction
 */
export async function getSquidStatus(
  params: {
    requestId?: string
    transactionId?: string
    chainflipStatusTrackingId?: string
    bridgeType?: 'chainflip' | 'chainflipmultihop'
  }
): Promise<SquidStatus> {
  try {
    const queryParams = new URLSearchParams()
    
    if (params.requestId) queryParams.append('requestId', params.requestId)
    if (params.transactionId) queryParams.append('transactionId', params.transactionId)
    if (params.chainflipStatusTrackingId) {
      queryParams.append('chainflipStatusTrackingId', params.chainflipStatusTrackingId)
      queryParams.append('bridgeType', params.bridgeType || 'chainflipmultihop')
    }

    const response = await fetch(`${SQUID_API_URL}/status?${queryParams.toString()}`, {
      headers: {
        'x-integrator-id': SQUID_INTEGRATOR_ID,
      },
    })

    if (!response.ok) {
      // Try to parse error response body
      let errorMessage = response.statusText
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || errorMessage
      } catch {
        // If JSON parsing fails, use statusText
      }
      
      const error = new Error(errorMessage) as Error & { status: number }
      error.status = response.status
      throw error
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.warn('⚠️ [Squid] Status check failed:', error)
    throw error
  }
}

/**
 * Get supported chains and tokens
 */
export async function getSquidChains() {
  try {
    const response = await fetch(`${SQUID_API_URL}/chains`)
    if (!response.ok) {
      throw new Error(`Squid API error: ${response.statusText}`)
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching Squid chains:', error)
    throw error
  }
}

/**
 * Get supported tokens for a chain
 */
export async function getSquidTokens(chainId: string) {
  try {
    const response = await fetch(`${SQUID_API_URL}/tokens?chainId=${chainId}`)
    if (!response.ok) {
      throw new Error(`Squid API error: ${response.statusText}`)
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching Squid tokens:', error)
    throw error
  }
}

// Chain IDs for Squid Router
// Source: https://docs.squidrouter.com/api-and-sdk-integration/new-bitcoin-and-solana
export const SQUID_CHAINS = {
  BITCOIN: 'bitcoin',          // Bitcoin Mainnet
  ETHEREUM: '1',               // Ethereum Mainnet
  ARBITRUM: '42161',
  OPTIMISM: '10',
} as const

// Token addresses for bridging
// Source: https://docs.squidrouter.com/api-and-sdk-integration/new-bitcoin-and-solana
export const SQUID_TOKENS = {
  BTC: 'satoshi',              // Native Bitcoin (NOT ERC20 format!)
  cbBTC_ETH: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf', // cbBTC on Ethereum
  WBTC_ETH: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', // WBTC on Ethereum
  WETH: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
  USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
} as const

/**
 * Convert BTC amount to satoshis (smallest unit)
 * 1 BTC = 100,000,000 satoshis
 */
function btcToSatoshis(btcAmount: string): string {
  const btc = parseFloat(btcAmount)
  const satoshis = Math.floor(btc * 100000000) // 10^8 satoshis per BTC
  return satoshis.toString()
}

/**
 * Compare rates for cbBTC vs WBTC and return the best option
 * 
 * @param btcAmount - BTC amount in decimal format (e.g., "0.0011")
 * @param userEthWalletAddress - User's Ethereum wallet address (where wrapped BTC is received)
 * @param slippage - Slippage tolerance (default 1%)
 * @param userBtcAddress - User's Bitcoin wallet address (where BTC is sent from)
 */
export async function getBestBTCRoute(
  btcAmount: string,
  userEthWalletAddress: Address,
  slippage: number = 1,
  userBtcAddress?: string
): Promise<{
  route: SquidRoute
  token: 'cbBTC' | 'WBTC'
  toTokenAddress: string
}> {
  try {
    // Convert BTC to satoshis for Squid Router API
    const amountInSatoshis = btcToSatoshis(btcAmount)
    
    console.log('🔢 [Squid] Amount conversion:', {
      btcAmount,
      satoshis: amountInSatoshis,
      userBtcAddress,
      userEthWalletAddress,
    })

    // Build params - fromAddress is Bitcoin address, toAddress is Ethereum address
    const cbBTCParams = {
      fromChain: SQUID_CHAINS.BITCOIN,
      fromToken: SQUID_TOKENS.BTC,
      fromAmount: amountInSatoshis,
      toChain: SQUID_CHAINS.ETHEREUM,
      toToken: SQUID_TOKENS.cbBTC_ETH,
      toAddress: userEthWalletAddress,
      fromAddress: userBtcAddress || userEthWalletAddress, // Bitcoin address if provided, else Ethereum
      slippage: slippage,
      enableBoost: true,
    }
    
    const wbtcParams = {
      fromChain: SQUID_CHAINS.BITCOIN,
      fromToken: SQUID_TOKENS.BTC,
      fromAmount: amountInSatoshis,
      toChain: SQUID_CHAINS.ETHEREUM,
      toToken: SQUID_TOKENS.WBTC_ETH,
      toAddress: userEthWalletAddress,
      fromAddress: userBtcAddress || userEthWalletAddress, // Bitcoin address if provided, else Ethereum
      slippage: slippage,
      enableBoost: true,
    }
    
    console.log('🔍 [Squid] Getting routes for both cbBTC and WBTC...')

    const [cbBTCRoute, wbtcRoute] = await Promise.all([
      getSquidRoute(cbBTCParams),
      getSquidRoute(wbtcParams),
    ])

    // Compare the output amounts
    const cbBTCAmount = parseFloat(cbBTCRoute.route.estimate.toAmount)
    const wbtcAmount = parseFloat(wbtcRoute.route.estimate.toAmount)

    console.log('🔍 [Squid] Rate comparison:', {
      cbBTC: {
        amount: cbBTCAmount,
        rate: cbBTCRoute.route.estimate.exchangeRate,
      },
      WBTC: {
        amount: wbtcAmount,
        rate: wbtcRoute.route.estimate.exchangeRate,
      },
    })

    // Return the route with the higher output
    if (cbBTCAmount >= wbtcAmount) {
      return {
        route: cbBTCRoute,
        token: 'cbBTC',
        toTokenAddress: SQUID_TOKENS.cbBTC_ETH,
      }
    } else {
      return {
        route: wbtcRoute,
        token: 'WBTC',
        toTokenAddress: SQUID_TOKENS.WBTC_ETH,
      }
    }
  } catch (error) {
    console.error('Error getting best BTC route:', error)
    throw error
  }
}

/**
 * Complete BTC deposit flow: get route, generate deposit address
 * 
 * @param btcAmount - BTC amount in decimal format
 * @param userEthWalletAddress - User's Ethereum wallet address (where wrapped BTC is received)
 * @param slippage - Slippage tolerance (default 1%)
 * @param userBtcAddress - Optional: User's Bitcoin wallet address (where BTC is sent from)
 */
export async function initiateBTCDeposit(
  btcAmount: string,
  userEthWalletAddress: Address,
  slippage: number = 1,
  userBtcAddress?: string
): Promise<{
  depositInfo: SquidDepositAddress
  token: 'cbBTC' | 'WBTC'
  route: SquidRoute
}> {
  console.log('🚀 [BTC Deposit] Starting two-step process...')
  
  // Step 1: Get best route WITH fromAddress (Bitcoin address)
  const { route, token } = await getBestBTCRoute(btcAmount, userEthWalletAddress, slippage, userBtcAddress)
  console.log('✅ [BTC Deposit] Step 1 complete - route received')

  // Step 2: Generate Bitcoin deposit address
  const depositInfo = await getSquidDepositAddress(route.route, route.requestId)
  console.log('✅ [BTC Deposit] Step 2 complete - deposit address generated')

  return {
    depositInfo,
    token,
    route,
  }
}

// Local storage key for deposit history
const DEPOSIT_HISTORY_KEY = 'btc_deposit_history'

/**
 * Save deposit to local history
 */
export function saveBTCDepositToHistory(deposit: BTCDepositHistory): void {
  try {
    const history = getBTCDepositHistory()
    history.unshift(deposit) // Add to beginning
    
    // Keep only last 50 deposits
    const trimmed = history.slice(0, 50)
    
    localStorage.setItem(DEPOSIT_HISTORY_KEY, JSON.stringify(trimmed))
  } catch (error) {
    console.error('Error saving deposit history:', error)
  }
}

/**
 * Get deposit history from local storage
 */
export function getBTCDepositHistory(): BTCDepositHistory[] {
  try {
    const stored = localStorage.getItem(DEPOSIT_HISTORY_KEY)
    if (!stored) return []
    
    const history: BTCDepositHistory[] = JSON.parse(stored)
    return history
  } catch (error) {
    console.error('Error loading deposit history:', error)
    return []
  }
}

/**
 * Update deposit status in history
 */
export function updateBTCDepositStatus(
  id: string,
  updates: Partial<Pick<BTCDepositHistory, 'status' | 'txHash' | 'completedAt' | 'errorMessage'>>
): void {
  try {
    const history = getBTCDepositHistory()
    const index = history.findIndex(d => d.id === id)
    
    if (index !== -1) {
      history[index] = { ...history[index], ...updates }
      localStorage.setItem(DEPOSIT_HISTORY_KEY, JSON.stringify(history))
    }
  } catch (error) {
    console.error('Error updating deposit status:', error)
  }
}

/**
 * Clear old completed/failed deposits (older than 30 days)
 */
export function clearOldBTCDeposits(): void {
  try {
    const history = getBTCDepositHistory()
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000)
    
    const filtered = history.filter(deposit => {
      // Keep pending/ongoing deposits
      if (deposit.status === 'pending' || deposit.status === 'deposited' || deposit.status === 'bridging') {
        return true
      }
      
      // Keep recent completed/failed
      return deposit.createdAt > thirtyDaysAgo
    })
    
    localStorage.setItem(DEPOSIT_HISTORY_KEY, JSON.stringify(filtered))
  } catch (error) {
    console.error('Error clearing old deposits:', error)
  }
}
